import { formatDuration, VIDEO_CHUNK_BYTES, VIDEO_MAX_BYTES, VIDEO_MAX_SECONDS, type MediaStatusResponse } from "@kmstories/shared";
import Uppy from "@uppy/core";
import Tus from "@uppy/tus";
import { useEffect, useRef, useState } from "react";
import { useT } from "../i18n/LocaleProvider.js";
import { api } from "../lib/api.js";
import type { FormId } from "../lib/useFormToken.js";

/**
 * Resumable video upload via tus: ~6 MB chunks, so every request finishes in
 * seconds (Traefik's 60 s body timeout is never hit) and a dropped 4G
 * connection resumes from the last chunk instead of restarting. The file's
 * bytes are streamed straight to the server's quarantine directory.
 */
type Phase =
  | { phase: "idle" }
  | { phase: "uploading"; progress: number; paused: boolean; fileId: string }
  | { phase: "processing"; mediaId: string; since: number }
  | { phase: "ready"; mediaId: string; durationSeconds?: number }
  | { phase: "error"; message: string };

export function VideoUploader({
  getToken,
  onReady,
  onReset,
  initial,
}: {
  getToken: (formId: FormId) => Promise<string>;
  onReady: (mediaId: string) => void;
  onReset: () => void;
  initial?: { mediaId: string } | null;
}) {
  const { m, locale } = useT();
  const [state, setState] = useState<Phase>(initial ? { phase: "ready", mediaId: initial.mediaId } : { phase: "idle" });
  const [dragging, setDragging] = useState(false);
  const uppy = useRef<Uppy | null>(null);

  useEffect(() => {
    const u = new Uppy({
      autoProceed: true,
      allowMultipleUploadBatches: false,
      restrictions: { maxNumberOfFiles: 1, maxFileSize: VIDEO_MAX_BYTES, allowedFileTypes: ["video/*"] },
    }).use(Tus, {
      endpoint: "/api/upload/tus",
      chunkSize: VIDEO_CHUNK_BYTES,
      retryDelays: [0, 1000, 3000, 5000, 10000, 20000],
      removeFingerprintOnSuccess: true,
      withCredentials: true,
      allowedMetaFields: ["kind", "formToken", "filename", "filetype", "name", "type"],
    });
    u.on("upload-progress", (file, p) => {
      if (!file) return;
      setState({ phase: "uploading", progress: p.bytesTotal ? p.bytesUploaded / p.bytesTotal : 0, paused: false, fileId: file.id });
    });
    u.on("upload-success", (_file, response) => {
      const id = response.uploadURL?.split("/").pop();
      if (!id) {
        setState({ phase: "error", message: m.share.uploader.failed });
        return;
      }
      setState({ phase: "processing", mediaId: id, since: Date.now() });
    });
    u.on("upload-error", () => setState({ phase: "error", message: m.share.uploader.failed }));
    u.on("restriction-failed", (_file, err) => {
      const tooBig = /size|large/i.test(err.message);
      setState({
        phase: "error",
        message: tooBig ? m.share.uploader.tooLarge.replace("{max}", `${Math.round(VIDEO_MAX_BYTES / 1024 / 1024)} ${m.common.mb}`) : m.share.uploader.rejected,
      });
    });
    uppy.current = u;
    return () => {
      u.destroy();
      uppy.current = null;
    };
  }, [m]);

  useEffect(() => {
    if (state.phase !== "processing") return;
    const { mediaId, since } = state;
    const h = window.setInterval(async () => {
      try {
        const st = await api.get<MediaStatusResponse>(`/api/upload/status/${mediaId}`);
        if (st.status === "ready") {
          setState({ phase: "ready", mediaId, durationSeconds: st.durationSeconds });
          onReady(mediaId);
        } else if (st.status === "rejected") {
          const msg = st.reason === "too_long" ? m.share.uploader.tooLong.replace("{max}", `${VIDEO_MAX_SECONDS / 60} ${m.common.minutes}`) : m.share.uploader.rejected;
          setState({ phase: "error", message: msg });
        } else if (Date.now() - since > 600_000) {
          setState({ phase: "error", message: m.share.uploader.stuck });
        }
      } catch {
        if (Date.now() - since > 600_000) setState({ phase: "error", message: m.share.uploader.stuck });
      }
    }, 2500);
    return () => window.clearInterval(h);
  }, [state, onReady, m]);

  async function addFile(file: File) {
    const u = uppy.current;
    if (!u) return;
    try {
      const formToken = await getToken("upload");
      u.cancelAll();
      u.setMeta({ kind: "video", formToken });
      u.addFile({ name: file.name, type: file.type || "video/mp4", data: file, source: "local" });
      setState({ phase: "uploading", progress: 0, paused: false, fileId: "" });
    } catch {
      setState({ phase: "error", message: m.share.uploader.failed });
    }
  }

  function reset() {
    uppy.current?.cancelAll();
    setState({ phase: "idle" });
    onReset();
  }

  const up = m.share.uploader;

  return (
    <div className="recorder" aria-live="polite">
      {state.phase === "idle" && (
        <label
          className={`dropzone ${dragging ? "dropzone--active" : ""}`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            const f = e.dataTransfer.files?.[0];
            if (f) void addFile(f);
          }}
        >
          <span className="btn btn--primary">{up.choose}</span>
          <span className="muted small">{up.dropHint}</span>
          <span className="muted small">{m.share.formats.video.body}</span>
          <input
            className="file-input"
            type="file"
            accept="video/*"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void addFile(f);
            }}
          />
        </label>
      )}

      {state.phase === "uploading" && (
        <>
          <div className="progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(state.progress * 100)}>
            <div className="progress__fill" style={{ width: `${Math.round(state.progress * 100)}%` }} />
          </div>
          <div className="recorder__row">
            <span className="muted">{up.progress.replace("{percent}", String(Math.round(state.progress * 100)))}</span>
            {state.fileId && (
              <button
                type="button"
                className="btn btn--ghost btn--small"
                onClick={() => {
                  const paused = uppy.current?.pauseResume(state.fileId) ?? false;
                  setState({ ...state, paused });
                }}
              >
                {state.paused ? up.resume : up.pause}
              </button>
            )}
            <button type="button" className="btn btn--ghost btn--small" onClick={reset}>
              {up.cancel}
            </button>
          </div>
        </>
      )}

      {state.phase === "processing" && <p className="muted">{up.processing}…</p>}

      {state.phase === "ready" && (
        <div className="recorder__row">
          <span className="badge badge--ok">{up.done}</span>
          {state.durationSeconds != null && <span className="muted">{formatDuration(state.durationSeconds, locale)}</span>}
          <button type="button" className="btn btn--ghost btn--small" onClick={reset}>
            {up.cancel}
          </button>
        </div>
      )}

      {state.phase === "error" && (
        <div className="notice notice--danger" role="alert">
          <p style={{ margin: 0 }}>{state.message}</p>
          <button type="button" className="btn btn--ghost btn--small" style={{ marginBlockStart: "0.6rem" }} onClick={reset}>
            {m.common.retry}
          </button>
        </div>
      )}
    </div>
  );
}
